import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle, ArrowLeft, Package, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("polling"); // polling, paid, failed
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (sessionId) pollStatus(0);
    else setStatus("failed");
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 8) { setStatus("failed"); return; }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentData(res.data);
      if (res.data.payment_status === "paid" || res.data.payment_status === "authorized") {
        setStatus("paid");
        sessionStorage.removeItem("order_data");
      } else if (res.data.status === "expired") {
        setStatus("failed");
      } else {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" data-testid="checkout-success-page">
      <div className="brutal-card max-w-lg w-full p-12 text-center">
        {status === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 size={56} className="mx-auto mb-6 animate-spin text-[#FF3366]" strokeWidth={2.5} />
            <h2 className="font-heading text-2xl font-bold uppercase mb-3">Processing Payment...</h2>
            <p className="text-zinc-500 text-sm">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#00E5FF] border-2 border-black flex items-center justify-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <CheckCircle size={40} strokeWidth={2.5} />
            </div>
            <h2 className="font-heading text-2xl font-bold uppercase mb-3">Order Confirmed!</h2>
            <p className="text-zinc-500 text-sm mb-6">Thank you for your order. Your payment has been authorized and we&apos;ll review your design shortly. You&apos;ll only be charged once your engraving is approved and completed.</p>
            {paymentData && (
              <div className="border-2 border-black p-4 mb-6 text-left bg-zinc-50">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-500">Amount Authorized:</span>
                  <span className="font-heading font-bold">${(paymentData.amount_total / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Order:</span>
                  <span className="font-mono text-xs">{paymentData.metadata?.order_id?.slice(0, 8) || "—"}</span>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate("/track")} className="brutal-btn brutal-btn-secondary text-sm flex items-center gap-2" data-testid="track-order-btn">
                <Search size={14} strokeWidth={2.5} /> Track Order
              </button>
              <button onClick={() => navigate("/")} className="brutal-btn text-sm flex items-center gap-2" data-testid="back-to-shop-btn">
                <Package size={14} strokeWidth={2.5} /> Back to Shop
              </button>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="payment-failed">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#FF3366] text-white border-2 border-black flex items-center justify-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <XCircle size={40} strokeWidth={2.5} />
            </div>
            <h2 className="font-heading text-2xl font-bold uppercase mb-3">Payment Issue</h2>
            <p className="text-zinc-500 text-sm mb-6">We couldn&apos;t confirm your payment. Please try again or contact us.</p>
            <button onClick={() => navigate("/")} className="brutal-btn text-sm flex items-center gap-2" data-testid="retry-btn">
              <ArrowLeft size={14} strokeWidth={2.5} /> Back to Shop
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
